import { useEffect, useRef } from 'react';
import { useBladeStack } from './BladeStackProvider';
import { getViewPrimitive, type BladeContext } from './registry';
import { useBladeLiveness, livenessLabel, type LivenessStatus } from './useBladeLiveness';
import type { BladeDescriptor } from './types';

/** Blade-header liveness dot colour per status (theme-aware for offline). */
const LIVENESS_COLOR: Record<LivenessStatus, string> = {
  live: 'var(--ch-blue-600)',
  reconnecting: 'var(--ch-amber-500)',
  offline: 'var(--border-strong)',
};

// The blade canvas (HUB-275). Explorer = blades as columns at their per-(entity,view)
// width (narrow/med/wide/xwide); focus = the active blade full-bleed with all card
// chrome stripped (no gutter, border, radius, shadow, or active accent) — matches
// the agreed mockup. Visual — needs ux-review.

/** Lucide "x" close icon (no decorative unicode glyphs, per the design system). */
function CloseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function BladeCanvas() {
  const { state } = useBladeStack();
  const focus = state.mode === 'focus';
  const canvasRef = useRef<HTMLDivElement>(null);

  // Keep the active blade in view. In explorer mode the canvas scrolls
  // horizontally; opening a child (or stepping back) off the visible edge should
  // scroll it into the viewport, matching the mockup. (Focus shows a single
  // blade, so there is nothing to scroll.)
  useEffect(() => {
    if (focus) return;
    const el = canvasRef.current?.querySelector<HTMLElement>('.blade.active');
    if (el && typeof el.scrollIntoView === 'function') {
      try {
        el.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      } catch {
        /* jsdom has no layout engine — scrollIntoView is a no-op in tests */
      }
    }
  }, [state.activeIndex, state.blades.length, focus]);

  if (state.blades.length === 0) {
    return <div className="empty">No blades open.</div>;
  }

  // Focus mode: if the active blade was drilled from a list, keep that list as a
  // 360px master rail beside the full-width detail (master/detail split, matching
  // the mockup). Otherwise the active blade goes full-bleed.
  if (focus && state.activeIndex >= 0) {
    const ai = state.activeIndex;
    const parent = ai > 0 ? state.blades[ai - 1] : undefined;
    const split = !!parent && parent.key === 'list';
    return (
      <div ref={canvasRef} className={split ? 'canvas focus split' : 'canvas focus'}>
        {split && (
          <Blade key={ai - 1} descriptor={state.blades[ai - 1]} index={ai - 1} variant="master" />
        )}
        <Blade key={ai} descriptor={state.blades[ai]} index={ai} variant={split ? 'detail' : undefined} />
      </div>
    );
  }

  return (
    <div ref={canvasRef} className="canvas">
      {state.blades.map((descriptor, i) => (
        <Blade key={i} descriptor={descriptor} index={i} />
      ))}
    </div>
  );
}

function Blade({
  descriptor,
  index,
  variant,
}: {
  descriptor: BladeDescriptor;
  index: number;
  /** focus-mode master/detail role (adds the .master / .detail split class). */
  variant?: 'master' | 'detail';
}) {
  const { state, dispatch } = useBladeStack();
  const active = index === state.activeIndex;
  const primitive = getViewPrimitive(descriptor.key);
  const width = descriptor.width ?? primitive?.defaultWidth ?? 'med';
  // A decoded blade is titled with its own key (the codec carries no display
  // strings), so treat that as "no title" and fall back to the view's own.
  const title = descriptor.title === descriptor.key ? (primitive?.defaultTitle ?? descriptor.title) : descriptor.title;
  const eyebrow = descriptor.eyebrow ?? primitive?.defaultEyebrow;
  const liveness = useBladeLiveness(descriptor);

  const ctx: BladeContext = {
    descriptor,
    index,
    openChild: (blade) => dispatch({ type: 'open', at: index, blade }),
    // Open at index-1: keeps blades[0..index-1], drops this blade + descendants,
    // pushes the replacement — an in-place swap that preserves the parent path.
    replaceSelf: (blade) => dispatch({ type: 'open', at: index - 1, blade }),
    setMode: (mode) => dispatch({ type: 'setMode', mode }),
    close: () => dispatch({ type: 'close', index }),
  };

  return (
    <section
      data-testid="blade"
      onClick={() => dispatch({ type: 'activate', index })}
      aria-current={active}
      className={['blade', `w-${width}`, active ? 'active' : '', variant ?? '']
        .filter(Boolean)
        .join(' ')}
    >
      {!(primitive?.chromeless || descriptor.chromeless) && (
        <header className="blade-head">
          <div className="toprow">
            <div style={{ minWidth: 0 }}>
              {eyebrow && <div className="blade-eyebrow">{eyebrow}</div>}
              <h2 className="blade-title">{title}</h2>
            </div>
            <span
              role="img"
              aria-label={livenessLabel(liveness)}
              title={livenessLabel(liveness)}
              className={`live-dot live-${liveness}`}
              style={{ background: LIVENESS_COLOR[liveness] }}
            />
          </div>
          <button
            type="button"
            className="blade-close"
            title="Close"
            aria-label="Close blade"
            onClick={(e) => {
              e.stopPropagation();
              ctx.close();
            }}
          >
            <CloseIcon />
          </button>
        </header>
      )}
      <div className="blade-body">
        {primitive ? (
          primitive.render(ctx)
        ) : (
          <div style={{ fontSize: 13, color: 'var(--fg-tertiary)' }}>
            Unknown view “{descriptor.key}”.
          </div>
        )}
      </div>
    </section>
  );
}
